import { useState, useEffect, useCallback } from 'react';
import { socialMediaApi } from '../api';

export type BrandSettingsData = Awaited<
  ReturnType<typeof socialMediaApi.getBrandSettings>
>['settings'];

export interface UseBrandSettingsResult {
  settings: BrandSettingsData | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadSettings: () => Promise<void>;
  saveSettings: (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[0]) => Promise<void>;
}

export function useBrandSettings(): UseBrandSettingsResult {
  const [settings, setSettings] = useState<BrandSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.getBrandSettings();
      setSettings(result.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load brand settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveSettings = useCallback(
    async (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[0]) => {
      setIsSaving(true);
      setError(null);
      try {
        const result = await socialMediaApi.upsertBrandSettings(data);
        setSettings(result.settings);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save brand settings');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return { settings, isLoading, isSaving, error, loadSettings, saveSettings };
}
